<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'

const route = useRoute()
const isOpen = ref(false)
const menuButton = ref<HTMLButtonElement | null>(null)
const menuPanel = ref<HTMLElement | null>(null)

const links = [
  { label: 'Home', to: '/' },
  { label: 'Ship', to: '/ship' },
  { label: 'Track', to: '/track' },
  { label: 'Get a quote', to: '/quote' },
  { label: 'Services', to: '/ship' },
  { label: 'Locations', to: '/locations' },
  { label: 'Rewards', to: '/rewards' },
  { label: 'FAQs', to: '/help' },
  { label: 'Contact', to: '/contact' },
]

function setBackgroundInert(value: boolean) {
  document
    .querySelectorAll<HTMLElement>('.site-main, .site-footer, .mobile-header, .mobile-dock')
    .forEach((element) => {
      element.inert = value
    })
}

function closeMenu(restoreFocus = false) {
  isOpen.value = false
  if (restoreFocus) nextTick(() => menuButton.value?.focus())
}

function onKeydown(event: KeyboardEvent) {
  if (!isOpen.value) return
  if (event.key === 'Escape') {
    event.preventDefault()
    closeMenu(true)
    return
  }

  if (event.key !== 'Tab' || !menuPanel.value) return
  const focusable = Array.from(
    menuPanel.value.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  )
  const first = focusable[0]
  const last = focusable.at(-1)
  if (!first || !last) return
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

watch(isOpen, async (open) => {
  document.body.classList.toggle('nav-open', open)
  setBackgroundInert(open)
  if (open) {
    await nextTick()
    menuPanel.value?.querySelector<HTMLElement>('a[href]')?.focus()
  }
})

watch(
  () => route.fullPath,
  () => closeMenu(),
)

onBeforeUnmount(() => {
  document.body.classList.remove('nav-open')
  setBackgroundInert(false)
})
</script>

<template>
  <header class="mobile-header">
    <RouterLink class="mobile-header__brand" to="/" aria-label="Carib Wave Logistics home">
      <picture>
        <source srcset="/logo-256.avif" type="image/avif" />
        <source srcset="/logo-256.webp" type="image/webp" />
        <img src="/logo-256.png" alt="" width="256" height="256" />
      </picture>
      <span>Carib Wave</span>
    </RouterLink>
    <div class="mobile-header__actions">
      <a class="mobile-header__login" href="https://portal.caribwaveja.com">Log in</a>
      <button
        ref="menuButton"
        class="menu-toggle"
        type="button"
        :aria-expanded="isOpen"
        aria-controls="mobile-menu"
        :aria-label="isOpen ? 'Close navigation' : 'Open navigation'"
        @click="isOpen ? closeMenu(true) : (isOpen = true)"
      >
        <span></span><span></span>
      </button>
    </div>
  </header>

  <Transition name="menu-fade">
    <div v-if="isOpen" class="mobile-menu__backdrop" @click.self="closeMenu(true)">
      <section
        id="mobile-menu"
        ref="menuPanel"
        class="mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        @keydown="onKeydown"
      >
        <div class="mobile-menu__heading">
          <span class="eyebrow">Route directory</span>
          <button class="menu-close" type="button" aria-label="Close navigation" @click="closeMenu(true)">
            Close
          </button>
        </div>
        <nav aria-label="Mobile navigation">
          <RouterLink v-for="(link, index) in links" :key="link.to" :to="link.to">
            <span aria-hidden="true">{{ String(index).padStart(2, '0') }}</span>
            {{ link.label }}
            <span aria-hidden="true">↗</span>
          </RouterLink>
        </nav>
        <div class="mobile-menu__account" aria-label="Account actions">
          <a class="button mobile-menu__login" href="https://portal.caribwaveja.com">Log in</a>
          <a class="button button--signal mobile-menu__signup" href="https://portal.caribwaveja.com/signup">Create an account</a>
        </div>
      </section>
    </div>
  </Transition>

  <nav class="mobile-dock" aria-label="Quick actions">
    <a href="https://portal.caribwaveja.com">
      <span aria-hidden="true">↳</span>
      Log in
    </a>
    <a class="mobile-dock__signup" href="https://portal.caribwaveja.com/signup">
      <span aria-hidden="true">↗</span>
      Sign up
    </a>
    <RouterLink to="/track">
      <span aria-hidden="true">⌁</span>
      Track
    </RouterLink>
  </nav>
</template>
